import React, { useRef, useState } from "react";
import { Grid, InputAdornment } from "@mui/material";
import { StriimTypography, StriimInputField, StriimButton, StriimForm } from "@striim/striim-ui";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { McpCard } from "../mcp-card/mcp-card";
import { McpTokenService, McpTokenResult } from "../../services/mcp-token-service";
import growl from "app/components/common/growl";
import styles from "./token-generator-panel.styles";

interface TokenGeneratorPanelProps {
    mcpTokenService: McpTokenService;
    onTokenGenerated: (result: McpTokenResult) => void;
}

const TokenGeneratorPanel: React.FC<TokenGeneratorPanelProps> = ({ mcpTokenService, onTokenGenerated }) => {
    const formRef = useRef<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleGenerateToken = async () => {
        const password = formRef.current?.values?.password?.trim();
        if (!password) {
            setError("Enter your password to generate a token.");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await mcpTokenService.generateToken(password);
            growl.success("Token generated successfully");
            onTokenGenerated(result);
            formRef.current?.resetForm();
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : "Failed to generate token";
            setError(errorMsg);
            growl.error(errorMsg, "Token Generation Error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <McpCard>
            <Grid container direction="column" gap={2}>
                <Grid item>
                    <StriimTypography variant="h2" sx={styles.title}>
                        Generate MCP Token
                    </StriimTypography>
                    <StriimTypography variant="body4" sx={styles.description}>
                        Enter your password to generate a session token for MCP client authentication. The token is
                        scoped to MCP access only and does not affect your current browser session.
                    </StriimTypography>
                </Grid>

                <Grid item xs={12}>
                    <StriimForm values={{ password: "" }} formRef={formRef} formProps={{ style: { width: "100%" } }}>
                        {() => (
                            <Grid container spacing={1} alignItems={error ? "center" : "flex-end"}>
                                <Grid item xs={9}>
                                    <StriimInputField
                                        isFormElement
                                        name="password"
                                        label="Password"
                                        type="password"
                                        previewPassword
                                        disabled={isLoading}
                                        error={!!error}
                                        helperText={error}
                                        placeholder="Enter your password"
                                        data-testid="password-input"
                                        fullWidth
                                        onChange={() => setError(null)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start" sx={styles.passwordAdornment}>
                                                    <LockOutlinedIcon fontSize="small" />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <StriimButton
                                        variant="primary"
                                        onClick={handleGenerateToken}
                                        disabled={!formRef.current?.values?.password?.trim() || isLoading}
                                        data-testid="generate-button"
                                        fullWidth
                                    >
                                        {isLoading ? "Generating token…" : "Generate token"}
                                    </StriimButton>
                                </Grid>
                            </Grid>
                        )}
                    </StriimForm>
                </Grid>
            </Grid>
        </McpCard>
    );
};

export default TokenGeneratorPanel;
